import { ArrowLeft, Grid2x2Plus, PanelRightClose } from "lucide-react";
import { Button } from "~/components/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "~/components/ui/tooltip";
import { cn } from "~/lib/utils";
import type { SessionIngest } from "~/pages/manifold-nextgen/components/conversations/useSessionIngests";
import { useIngestStatus } from "~/pages/manifold-nextgen/hooks/useIngestStatus";
import { ingestStatusDisplay } from "./SessionContextIngestDetail";

interface SessionContextIngestListProps {
  ingests: SessionIngest[];
  onOpenIngest: (jobId: string) => void;
  onBack: () => void;
  onCollapse: () => void;
}

// The full session ingestion list, reached from the single Apps > Ingestions
// entry. Each row shows the target table and its live status; selecting one
// opens that ingestion's detail. Mirrors `SessionContextAppsDetail`'s
// group-opens-a-panel shape.
export const SessionContextIngestList = ({
  ingests,
  onOpenIngest,
  onBack,
  onCollapse,
}: SessionContextIngestListProps) => {
  return (
    <div className="flex h-full flex-col overflow-hidden">
      <div className="flex h-14 shrink-0 items-center gap-1 border-b border-border px-2">
        <Button
          variant="ghost"
          size="icon"
          onClick={onBack}
          aria-label="Back to session context"
          className="size-8"
        >
          <ArrowLeft className="size-4" />
        </Button>
        <span className="truncate text-sm font-medium text-foreground">Ingestions</span>
        <Tooltip delayDuration={700}>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              onClick={onCollapse}
              aria-label="Collapse session context"
              className="ml-auto size-8"
            >
              <PanelRightClose className="size-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent side="bottom">Toggle Context</TooltipContent>
        </Tooltip>
      </div>

      <div className="flex-1 overflow-y-auto p-2">
        {ingests.length === 0 ? (
          <p className="p-4 text-center text-xs italic text-muted-foreground/80">
            No ingestions yet.
          </p>
        ) : (
          <ul className="flex flex-col gap-0.5">
            {ingests.map((ing) => (
              <IngestListRow key={ing.jobId} ingest={ing} onOpen={onOpenIngest} />
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

// One row: polls its own status (independent of any active turn) so the list
// stays live while open, and labels by the target table recovered from the
// create_ingest result (fresh poll wins when it has landed).
const IngestListRow = ({
  ingest,
  onOpen,
}: {
  ingest: SessionIngest;
  onOpen: (jobId: string) => void;
}) => {
  const { data } = useIngestStatus(ingest.jobId);
  const display = ingestStatusDisplay(data?.status);
  const label = ingestName(data?.table_fqn ?? ingest.tableFqn, ingest.jobId);

  return (
    <li>
      <button
        type="button"
        onClick={() => onOpen(ingest.jobId)}
        className="flex w-full min-w-0 cursor-pointer items-center gap-2.5 rounded-md px-2 py-1.5 text-left hover:bg-accent/50"
      >
        <Grid2x2Plus className="size-4 shrink-0 text-muted-foreground" aria-hidden />
        <span className="min-w-0 flex-1 truncate text-sm text-foreground">{label}</span>
        <span
          className={cn(
            "flex shrink-0 items-center gap-1 text-xs font-medium",
            display.isFailed
              ? "text-destructive"
              : display.isCompleted
                ? "text-muted-foreground"
                : "text-primary"
          )}
        >
          {display.isLive && (
            <span className="size-1.5 animate-pulse rounded-full bg-current" aria-hidden />
          )}
          {display.label}
        </span>
      </button>
    </li>
  );
};

// Target table's short name, or a compact job-id fallback when the result
// carried no table.
function ingestName(fqn: string | null, jobId: string): string {
  if (fqn) {
    return fqn.split(".").pop() || fqn;
  }
  return jobId.length > 8 ? `${jobId.slice(0, 8)}…` : jobId;
}
